import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  model,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  DxTextBoxModule,
  DxValidatorModule,
  DxFormModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxCheckBoxModule,
  DxTextAreaModule,
  DxNumberBoxModule,
  DxValidationGroupModule,
  DxFileUploaderModule,
  DxPopoverComponent,
  DxDropDownBoxModule,
  DxTreeViewModule,
} from 'devextreme-angular';
import { DxiValidationRuleModule, DxiItemModule, DxoLabelModule } from 'devextreme-angular/ui/nested';
import { SvgIconComponent } from '../../../../../../../components/svg-icon/svg-icon.component';
import { GpdPillComponent } from '../../../../../../../components/gpd-pill/gpd-pill.component';
import {
  ConfigTask,
  MappedConfigTask,
  TaskType,
  TaskConfigTypeConfig,
  taskTypesConfig,
  TaskTypes,
  TaskTypeText,
  TaskTypeEnum,
  MappedConfigTreeNode,
} from '../../datatypes-taskconfig';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { AppGlobals } from 'src/app/app.globals';
import { TemplatesService } from 'src/app/services/templates.service';
import { DataAccessService } from 'src/app/services/data-access.service';
import { ConfigService } from 'src/app/services/config.service';
import { TaskTagCatalogItem } from 'src/app/interfaces/entities/task-tag';
import { RouterLink } from '@angular/router';
import { IconName } from 'src/app/helpers/icon-exports';
import { ValueChangedEvent } from 'devextreme/ui/file_uploader';
import { AppConfig } from 'src/app/app.config';
import { FeatureFlags } from 'src/app/interfaces/enums/feature-flag';
import { TemplateManagementService } from 'src/app/services/template-management.service';
import { ContractService } from 'src/app/services/contracts/contract.service';
import { ContractStatusConfig } from 'src/app/constants/status-pill-configs';
import { TaskRule } from 'src/app/interfaces/entities/task-rule';
import { TaskRulesPopupComponent } from 'src/app/components/task-rules/task-rules-popup.component';

import { SearchableDropdownDirective } from '../../../../../../../directives/searchable-dropdown.directive';

/** Node of the navigable template tree: folders group, and — unlike fill-template-modal's tree —
 *  are themselves selectable ("use every template in this folder"), same as documents. */
interface TemplateTreeNode {
  id: string; // 'F<folderId>' | 'D<documentId>'
  parentId: string | null;
  name: string;
  icon: 'folder' | 'doc';
}

@Component({
    selector: 'app-config-task-node',
    imports: [
        CommonModule,
        FormsModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxiValidationRuleModule,
        DxFormModule,
        DxSelectBoxModule,
        DxTagBoxModule,
        DxCheckBoxModule,
        DxTextAreaModule,
        DxNumberBoxModule,
        DxValidationGroupModule,
        DxiItemModule,
        DxoLabelModule,
        SvgIconComponent,
        GpdPillComponent,
        GpdButtonComponent,
        DxFileUploaderModule,
        DxPopoverComponent,
        SearchableDropdownDirective,
        DxDropDownBoxModule,
        DxTreeViewModule,
        RouterLink,
        TaskRulesPopupComponent,
    ],
    templateUrl: './config-task-node.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './config-task-node.component.css'
})
export class ConfigTaskNodeComponent implements OnInit, OnDestroy {
  @Input() task!: MappedConfigTask;
  @Input() itemNumber!: number;
  @Input() allowEdit: boolean = true;
  @Input() allowDelete: boolean = true;
  @Input() archived: string | null | undefined = null;
  /** Template task packages from the structure tree, for "create a microtask". */
  taskPackages: { id: number; name: string }[] = [];
  private _projectConfiguration: MappedConfigTreeNode[] = [];
  // A field, not a getter: the rules popup binds it to a select box's [items] (new array per CD resets it).
  @Input() set projectConfiguration(nodes: MappedConfigTreeNode[]) {
    this._projectConfiguration = nodes ?? [];
    this.taskPackages = this._projectConfiguration
      .filter(n => n.nodeId.startsWith('taskPackage-'))
      .map(n => ({ id: Number(n.nodeId.replace('taskPackage-', '')), name: n.title }));
  }
  get projectConfiguration(): MappedConfigTreeNode[] {
    return this._projectConfiguration;
  }
  @Input() allTasks: ConfigTask[] = [];

  selected = model(false);

  @Output() onClick = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<{ task: MappedConfigTask; callback: (success: boolean) => void }>();
  @Output() onCancel = new EventEmitter<MappedConfigTask>();
  @Output() onDelete = new EventEmitter<void>();
  @Output() onStartEdit = new EventEmitter<void>();
  @Output() onAddTask = new EventEmitter<MappedConfigTask>();
  @Output() onArchive = new EventEmitter<void>();
  @Output() onReactivate = new EventEmitter<void>();

  get isArchived(): boolean {
    return !!this.archived;
  }

  get allowArchive(): boolean {
    return !this.isArchived && this.allowEdit;
  }

  // Data sources for form fields
  areaTypes: any[] = [];
  taskTags: TaskTagCatalogItem[] = [];
  departmentTypes: any[] = [];
  milestones: any[] = [];
  roles: any[] = [];
  gridDestinationPaths: any[] = [];
  projectDestinationPaths: any[] = [];
  fullDestinationPaths: any[] = [];

  TaskTypeEnum = TaskTypeEnum;
  allTaskTypes = TaskTypes;
  filteredTaskTypes = TaskTypes.filter(t => t.value !== TaskTypeEnum.Group);

  taskTypesConfig = taskTypesConfig;
  priorities: { id: number; icon: IconName; color: string; text: string }[] = [
    { id: 0, icon: 'bookmark', color: 'var(--color-red-500)', text: $localize`:@@critical:Critical` },
    { id: 1, icon: 'arrow-up-triple', color: 'var(--color-red-500)', text: $localize`:@@high:High` },
    { id: 2, icon: 'arrow-up-double', color: 'var(--color-yellow-500)', text: $localize`:@@medium:Medium` },
    { id: 3, icon: 'chevron-up', color: 'var(--color-green-500)', text: $localize`:@@low:Low` },
  ];
  requiredMessage: any = $localize`:@@required:Required`;

  editData: MappedConfigTask | null = null;

  /** #173: managed-template dropdown replaces the local uploader when the flag is on. */
  templateManagementEnabled = false;
  templateTree: TemplateTreeNode[] = [];
  templateDropDownOpened = false;
  selectedTemplateLabel: string | null = null;

  titleEditorOptions = {
    valueChangeEvent: 'input',
    elementAttr: { class: 'gpd-input' },
    validationMessageMode: 'always',
  };

  /** #4938: rules replace the display condition. */
  rulesVisible = false;
  users: { emailAddress: string; displayName: string }[] = [];
  contractTypes: { id: number; name: string }[] = [];
  readonly contractStatuses = ContractStatusConfig.map(s => ({ id: s.id, status: s.status }));

  private subscriptions: Subscription[] = [];

  constructor(
    private templatesService: TemplatesService,
    private templateManagementService: TemplateManagementService,
    private dataAccessService: DataAccessService,
    private configService: ConfigService,
    private contractService: ContractService,
    private cdr: ChangeDetectorRef
  ) {}

  get isNew(): boolean {
    return !this.task.id;
  }

  ngOnInit() {
    this.loadData();
    if (this.task.editing) {
      this.startEdit();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadData() {
    // Subscribe to area types
    this.subscriptions.push(
      this.templatesService.areaTypes$.subscribe(areaTypes => {
        this.areaTypes = areaTypes;
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.push(
      this.configService.taskTags$.subscribe(tags => {
        this.taskTags = tags;
        this.cdr.markForCheck();
      })
    );

    // Subscribe to departments
    this.subscriptions.push(
      this.dataAccessService.departments$.subscribe(departments => {
        this.departmentTypes = departments;
        this.cdr.markForCheck();
      })
    );

    // Subscribe to grid destination paths
    this.subscriptions.push(
      this.templatesService.gridFolders$.subscribe(gridDestinationPaths => {
        this.gridDestinationPaths = gridDestinationPaths;
        this.fullDestinationPaths = this.gridDestinationPaths.concat(this.projectDestinationPaths);
        this.cdr.markForCheck();
      })
    );

    // Subscribe to full destination paths
    this.subscriptions.push(
      this.templatesService.projectFolders$.subscribe(projectDestinationPaths => {
        this.projectDestinationPaths = projectDestinationPaths.map(x => ({
          ...x,
          name: $localize`:@@project:Project` + `/${x.name}`,
        }));
        this.fullDestinationPaths = this.gridDestinationPaths.concat(this.projectDestinationPaths);
        this.cdr.markForCheck();
      })
    );

    // Load initial data if not already loaded
    this.templatesService.loadAreaTypes();
    this.configService.loadTaskTags();
    this.dataAccessService.loadDepartments();
    this.templatesService.loadProjectFolders();
    this.templatesService.loadGridFolders();

    // One node per task: loadUserList() hands every node but the first the still-empty list
    // while the request runs, so take the list from the subject once it arrives.
    this.subscriptions.push(
      this.configService.userList$.subscribe(users => {
        this.users = users ?? [];
        this.cdr.markForCheck();
      })
    );
    this.configService.loadUserList();
    this.subscriptions.push(
      this.contractService.contractTypes$.subscribe(types => {
        this.contractTypes = (types ?? []).map((t: any) => ({ id: t.id, name: t.name }));
        this.cdr.markForCheck();
      })
    );
    if (AppGlobals.UserCan(FeatureFlags.Contracts)) this.contractService.loadContractTypes();
  }

  /** Active rules of a task: drives the count on the button and the icon in the tree. */
  readonly inactiveRuleTitle = $localize`:@@ruleInactive:Inactive`;

  activeRuleCount(task: MappedConfigTask | null): number {
    return task?.rules?.filter(r => r.isActive).length ?? 0;
  }

  onRulesApplied(rules: TaskRule[]) {
    if (!this.editData) return;
    this.editData.rules = rules;
    this.editData.rulesChanged = true;
    this.cdr.markForCheck();
  }

  startEdit() {
    this.task.editing = true;
    this.editData = { ...this.task };
    this.templateManagementEnabled = AppGlobals.UserCan(FeatureFlags.TemplateManagement);
    this.selectedTemplateLabel = null;
    if (this.templateManagementEnabled) {
      if (this.templateTree.length === 0) this.loadTemplateOptions();
      else this.syncSelectedTemplateLabel();
    }
    this.onStartEdit.emit();
  }

  /** Folders (F<id>, pick = every template in it) and documents (D<id>) as one real tree — unlike
   *  the old flat, path-labelled list, folders are browsable via their own children instead of
   *  every document repeating its full path in its label. */
  private async loadTemplateOptions(): Promise<void> {
    try {
      const tree = await this.templateManagementService.getTemplateTree();
      if (tree.hasError === true) return;
      const folders = (tree.folderList ?? []) as any[];
      const documents = (tree.documents ?? []) as any[];
      this.templateTree = [
        ...folders.map(f => ({
          id: `F${f.id}`,
          parentId: f.parentId != null ? `F${f.parentId}` : null,
          name: f.name,
          icon: 'folder' as const,
        })),
        ...documents.map(d => ({
          id: `D${d.id}`,
          parentId: d.folderId != null ? `F${d.folderId}` : null,
          name: d.name,
          icon: 'doc' as const,
        })),
      ].sort((a, b) => (a.icon === b.icon ? a.name.localeCompare(b.name) : a.icon === 'folder' ? -1 : 1));
      this.syncSelectedTemplateLabel();
      this.cdr.markForCheck();
    } catch {
      // dropdown stays empty; the field still renders and save is unaffected
    }
  }

  /** Restores the display label for an already-saved selection once the tree is loaded. */
  private syncSelectedTemplateLabel(): void {
    const key =
      this.editData?.templateDocumentId != null
        ? `D${this.editData.templateDocumentId}`
        : this.editData?.templateFolderId != null
          ? `F${this.editData.templateFolderId}`
          : null;
    this.selectedTemplateLabel = key != null ? (this.templateTree.find(n => n.id === key)?.name ?? null) : null;
  }

  /**
   * Both folders ("use every template in it") and documents commit on a row click, same as the
   * budget category tree — expanding/collapsing a folder happens via its own arrow instead
   * (no expandEvent="click" override here), so clicking the row never fights with drilling in.
   */
  selectTemplateNode(node: TemplateTreeNode): void {
    if (!this.editData) return;
    this.selectedTemplateLabel = node.name;
    this.templateDropDownOpened = false;
    this.editData.templateDocumentId = node.icon === 'doc' ? Number(node.id.slice(1)) : null;
    this.editData.templateFolderId = node.icon === 'folder' ? Number(node.id.slice(1)) : null;
    // a managed pick replaces any legacy ad-hoc upload (backend deletes the old blob)
    this.editData.templateFilename = null;
    this.editData.template = null;
    this.cdr.markForCheck();
  }

  clearTemplateSelection(): void {
    this.selectedTemplateLabel = null;
    if (!this.editData) return;
    this.editData.templateDocumentId = null;
    this.editData.templateFolderId = null;
    this.cdr.markForCheck();
  }

  /**
   * The field only ever changes via the tree (selectTemplateNode), which keeps this in sync with
   * selectedTemplateLabel exactly. acceptCustomValue lets the input also take free-typed text
   * (DevExtreme has no flat dataSource to validate against here) — if the committed value
   * doesn't match the last real selection, treat it as free text and clear rather than leave a
   * mismatched display next to a stale id that would still save.
   */
  onTemplateFieldValueChanged(e: any): void {
    if (e.value == null || e.value !== this.selectedTemplateLabel) this.clearTemplateSelection();
  }

  startAdd(parent: MappedConfigTask) {
    this.onAddTask.emit(parent);
  }

  async save() {
    if (this.editData) {
      console.log('node save before', this.editData);

      // Create a promise-based event emission
      const savePromise = new Promise<boolean>(resolve => {
        this.onSave.emit({
          task: this.editData!,
          callback: resolve,
        });
      });

      const success = await savePromise;

      if (success) {
        this.task = { ...this.editData };
        this.task.editing = false;
        this.editData = null;
        console.log('node save completed successfully');
      } else {
        console.log('node save failed, keeping editing state');
      }
    }
  }

  cancel() {
    this.task.editing = false;
    this.editData = null;
    this.onCancel.emit(this.task);
  }

  toggleExpanded() {
    this.task.expanded = !this.task.expanded;
  }

  getTaskTypeText(type: string): string {
    return TaskTypeText[type];
  }

  getTaskDepartmentText(departmentId: number): string {
    return this.departmentTypes.find(dt => dt.id === departmentId)?.name;
  }

  /** Tags whose catalog entry still exists, in catalog (alphabetical) order. */
  getTaskTags(tagIds: number[] | undefined): TaskTagCatalogItem[] {
    if (!tagIds?.length) return [];
    return this.taskTags.filter(tag => tagIds.includes(tag.id));
  }

  removeTag(tagId: number) {
    this.editData.tagIds = (this.editData.tagIds ?? []).filter(id => id !== tagId);
  }

  getAreaTypeText(areaTypeId: number): string {
    const areaType = this.areaTypes.find(at => at.id === areaTypeId);
    return areaType ? areaType.name : `Area ${areaTypeId}`;
  }

  onSiteTaskChanged(e: any) {
    this.editData.siteTask = e.value;
    if (this.editData && e.value) {
      this.editData.areaTypeIds = this.areaTypes.map(x => x.itemId);
    }
  }

  setAreaTypeValue = (rowData: any, value: any) => {
    rowData.areaTypeIds = value;
  };

  onTemplateFileChanged(e: ValueChangedEvent) {
    const file = e.value.at(0);
    if (file) {
      if (file.name.length > AppConfig.MaxFileNameLength) {
        AppGlobals.ShowErrorMessage(`File name ${file.name} is too long. Max length is ${AppConfig.MaxFileNameLength}`);
        return;
      }
      this.editData.templateFilename = e.value.at(0)?.name;
      this.editData.template = e.value.at(0);
    }
  }

  deleteTemplateFile() {
    this.editData.templateFilename = null;
    this.editData.template = null;
  }

  async downloadTemplate() {
    if (this.editData?.template) {
      // If there is a template file object, open it directly
      const url = URL.createObjectURL(this.editData.template);
      window.open(url);
    } else if (this.editData?.templateFilename) {
      // If there is a templateFilename with no template, get it from the API
      await this.getTemplateFile();
    }
  }

  async getTemplateFile() {
    const result = await this.templatesService.getTemplateFile({
      TemplateFileRequestItem: { filename: this.editData.templateFilename },
    });

    if (result.hasError) {
      AppGlobals.ShowErrorMessage(result.feedback);
    } else {
      if (result.fileResultItem.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else {
        let bytes = this.base64ToArrayBuffer(result.fileResultItem.fileBytes);
        let fName = this.editData.templateFilename;
        if (result.fileResultItem.fileName != null) fName = result.fileResultItem.fileName;
        this.saveByteArray(fName, result.fileResultItem.fileType, bytes);
      }
    }
  }

  base64ToArrayBuffer(base64: string): ArrayBuffer {
    let binaryString = window.atob(base64);
    let len = binaryString.length;
    let bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  saveByteArray(reportName, fileType, byte) {
    let mimeType = '';

    switch (fileType) {
      case 'png':
      case 'jpeg':
        mimeType = `image/${fileType}`;
        break;
      case 'csv':
      case 'txt':
        mimeType = `text/${fileType}`;
        break;
      case 'pdf':
      case 'json':
      case 'zip':
        mimeType = `application/${fileType}`;
        break;
      case 'xls':
        mimeType = `application/vnd.ms-excel`;
        break;
      case 'ppt':
        mimeType = `application/vnd.ms-powerpoint`;
        break;
      case 'odt':
        mimeType = `application/vnd.oasis.opendocument.text`;
        break;
      case 'pptx':
        mimeType = `application/vnd.openxmlformats-officedocument.presentationml.presentation`;
        break;
      case 'xlsx':
        mimeType = `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`;
        break;
      case 'docx':
        mimeType = `application/vnd.openxmlformats-officedocument.wordprocessingml.document`;
        break;
    }

    let blobClicked = new Blob([byte], { type: mimeType });
    let linkClicked = document.createElement('a');
    linkClicked.href = window.URL.createObjectURL(blobClicked);
    linkClicked.download = reportName;
    // linkClicked.click();
    window.open(linkClicked.href);
  }

  onSiteTaskChange(e: any) {
    if (e.value == true) {
      this.editData.areaTypeIds = this.areaTypes.map(x => x.id);
    }
  }
}
